import React, { useEffect, useState } from "react";
import { FaDownload, FaTrash, FaPlus } from "react-icons/fa";
import { useHasPrivilege } from "../../hooks/usePermissions";
import s from "./DocumentsPage.module.css";

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const hasPrivilege = useHasPrivilege();

  const canManageDocuments = hasPrivilege("MANAGE:DOCUMENTS");

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/documents");
      if (!response.ok) {
        throw new Error("Не удалось загрузить документы");
      }
      const data = await response.json();
      setDocuments(data);
    } catch (e) {
      setError(e.message || "Произошла ошибка при загрузке документов");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(`/api/files/${doc.path}/${doc.fileId}`);
      if (!response.ok) {
        throw new Error("Не удалось скачать файл");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileId || doc.title || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Ошибка при скачивании файла");
    }
  };

  const handleOpenUpload = () => {
    setNewTitle("");
    setNewFile(null);
    setShowUploadModal(true);
    setError("");
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newTitle || !newFile) {
      setError("Укажите название и выберите файл");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const fileId = newFile.name;

      const formData = new FormData();
      formData.append("title", newTitle);
      formData.append("fileId", fileId);
      formData.append("file", newFile);

      const uploadResponse = await fetch("/api/admin/documents", {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error("Не удалось загрузить документ");
      }

      setShowUploadModal(false);
      await fetchDocuments();
    } catch (e) {
      setError(e.message || "Ошибка при загрузке документа");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (doc) => {
    setDocumentToDelete(doc);
    setError("");
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/documents/${documentToDelete.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Не удалось удалить документ");
      }
      setDocumentToDelete(null);
      await fetchDocuments();
    } catch (e) {
      setError(e.message || "Ошибка при удалении документа");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={s.container}>
      <h1 className={s.title}>Документы Кировского комсомола</h1>
      <p className={s.subtitle}>Вы можете скачать следующие документы:</p>

      {canManageDocuments && (
        <div className={s.adminActions}>
          <button className={s.uploadButton} onClick={handleOpenUpload}>
            <FaPlus className={s.icon} /> Загрузить новый документ
          </button>
        </div>
      )}

      {loading && <div className={s.loading}>Загрузка...</div>}
      {error && <div className={s.error}>{error}</div>}

      <div className={s.documentsList}>
        <ul className={s.list}>
          {documents.map((doc) => (
            <li className={s.listItem} key={doc.id}>
              <button
                type="button"
                className={s.link}
                onClick={() => handleDownload(doc)}
              >
                <FaDownload className={s.icon} />
                <span className={s.text}>{doc.title}</span>
              </button>
              {canManageDocuments && (
                <button
                  type="button"
                  className={s.deleteButton}
                  onClick={() => confirmDelete(doc)}
                >
                  <FaTrash />
                </button>
              )}
            </li>
          ))}
          {documents.length === 0 && !loading && (
            <li className={`${s.listItem} ${s.listItemEmpty}`}>Документы пока не добавлены</li>
          )}
        </ul>
      </div>

      {showUploadModal && (
        <div className={s.modal}>
          <div className={s.modalContent}>
            <h2>Загрузить новый документ</h2>
            <form onSubmit={handleUpload}>
              <label className={s.label}>
                Название документа
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className={s.input}
                />
              </label>
              <label className={s.label}>
                Файл
                <input
                  type="file"
                  onChange={(e) => setNewFile(e.target.files[0])}
                  className={s.input}
                />
              </label>
              <div className={s.modalActions}>
                <button
                  type="submit"
                  className={`${s.saveButton} ${loading ? s.saveButtonDisabled : ""}`}
                  disabled={loading}
                >
                  {loading ? "Загрузка" : "Сохранить"}
                </button>
                <button
                  type="button"
                  className={s.cancelButton}
                  onClick={() => setShowUploadModal(false)}
                  disabled={loading}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {documentToDelete && (
        <div className={s.modal}>
          <div className={s.modalContent}>
            <h2>Удалить документ</h2>
            <p>Вы действительно хотите удалить документ «{documentToDelete.title}»?</p>
            <div className={s.modalActions}>
              <button className={s.deleteConfirmButton} onClick={handleDelete}>
                Удалить
              </button>
              <button
                className={s.cancelButton}
                onClick={() => setDocumentToDelete(null)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
